import React, { useCallback } from "react";
import { useQuery } from "@apollo/client/react";

import { KIT_ASSETS_QUERY } from "../constants/graphql/queries";
import useMutation from "../hooks/useMutation";
import {
  REMOVE_KIT_ASSET_MUTATION,
  UPDATE_KIT_ASSET_MUTATION,
} from "../constants/graphql/mutations";
import useNotification from "../hooks/notification";

const KitAssetsContext = React.createContext(null);

export const KitAssetsProvider = ({ children, id }) => {
  const { notifySuccess } = useNotification();
  const { refetch, loading, data: { asset = {} } = {} } = useQuery(KIT_ASSETS_QUERY, {
    variables: { id },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  const [remove] = useMutation(REMOVE_KIT_ASSET_MUTATION, {
    refetchQueries: [{ query: KIT_ASSETS_QUERY, variables: { id } }],
  });

  const [update] = useMutation(UPDATE_KIT_ASSET_MUTATION, {
    update(cache, data) {
      const updatedKitAsset = data.data.updateKitAsset.kitAsset;
      const { asset } = cache.readQuery({ query: KIT_ASSETS_QUERY, variables: { id } });
      cache.writeQuery({
        query: KIT_ASSETS_QUERY,
        data: {
          asset: {
            ...asset,
            kitAssets: asset.kitAssets.map(ka =>
              ka.id === updatedKitAsset.id ? { ...ka, quantity: updatedKitAsset.quantity } : ka
            ),
          },
        },
      });
    },
  });

  const removeKitAsset = useCallback(
    id => {
      return remove({
        variables: { id },
        onSuccess() {
          notifySuccess("Asset was removed");
        },
      });
    },
    [notifySuccess, remove]
  );

  const updateKitAsset = useCallback(
    (id, input) => {
      return update({
        variables: { id, input },
        onSuccess() {
          notifySuccess("Quantity was updated");
        },
      });
    },
    [notifySuccess, update]
  );

  return (
    <KitAssetsContext.Provider
      value={{ kit: asset, loading, refetch, removeKitAsset, updateKitAsset }}
    >
      {children}
    </KitAssetsContext.Provider>
  );
};

export const useKitAssets = () => React.useContext(KitAssetsContext);
